<template>
  <div class="ellipsis-box">
    <div v-html="formatText(expanded ? content : text)" class="text-box" ref="ellipsisRef"></div>
    <div v-if="hasAction" class="action" @click="onClickAction">
      {{ actionText }}
    </div>
  </div>
</template>
<script setup lang="ts">
import { formatText } from '@support/utils';
import { useI18n } from 'vue-i18n';
// 功能参考vant-ellipase
const props = defineProps({
  content: {
    type: String,
    default: ''
  },
  collapseText: {
    type: String,
    default: () => useI18n().t('selfRepair.btn_collapse')
  },
  expandText: {
    type: String,
    default: () => useI18n().t('selfRepair.btn_expand')
  },
  rows: {
    type: Number,
    default: 1
  }
});
const text = ref('');
const expanded = ref(false);
const hasAction = ref(false);
const ellipsisRef = ref();
const actionText = computed(() => (expanded.value ? props.collapseText : props.expandText));
onMounted(() => {
  calcEllipsised();
});
function calcEllipsised() {
  const cloneContainer = () => {
    const originStyle = window.getComputedStyle(ellipsisRef.value);
    const container = document.createElement('div');
    const styleNames: string[] = Array.prototype.slice.apply(originStyle);
    styleNames.forEach(name => {
      container.style.setProperty(name, originStyle.getPropertyValue(name));
    });

    container.style.position = 'fixed';
    container.style.zIndex = '-9999';
    container.style.top = '-9999px';
    container.style.height = 'auto';
    container.style.minHeight = 'auto';
    container.style.maxHeight = 'auto';

    container.innerText = props.content;
    document.body.appendChild(container);
    return container;
  };

  const calcEllipsisText = (container: HTMLDivElement, maxHeight: number) => {
    const { content } = props;
    const end = content.length;

    const calcEllipse = () => {
      // calculate the former or later content
      const tail = (left: number, right: number): string => {
        if (right - left <= 1) {
          return content.slice(0, left) + '...';
        }

        const middle = Math.round((left + right) / 2);

        // Set the interception location
        container.innerText = content.slice(0, middle) + '...' + actionText.value;

        // The height after interception still does not match the rquired height
        if (container.offsetHeight > maxHeight) {
          return tail(left, middle);
        }

        return tail(middle, right);
      };

      container.innerText = tail(0, end);
    };

    const middleTail = (leftPart: [number, number], rightPart: [number, number]): string => {
      if (leftPart[1] - leftPart[0] <= 1 && rightPart[1] - rightPart[0] <= 1) {
        return content.slice(0, leftPart[0]) + '...' + content.slice(rightPart[1], end);
      }

      const leftMiddle = Math.floor((leftPart[0] + leftPart[1]) / 2);
      const rightMiddle = Math.ceil((rightPart[0] + rightPart[1]) / 2);

      container.innerText =
        props.content.slice(0, leftMiddle) +
        '...' +
        props.content.slice(rightMiddle, end) +
        props.expandText;

      if (container.offsetHeight >= maxHeight) {
        return middleTail([leftPart[0], leftMiddle], [rightMiddle, rightPart[1]]);
      }

      return middleTail([leftMiddle, leftPart[1]], [rightPart[0], rightMiddle]);
    };

    calcEllipse();

    return container.innerText;
  };

  // Calculate the interceptional text
  const container = cloneContainer();
  if (!container) return;
  const { paddingBottom, paddingTop, lineHeight } = container.style;

  const maxHeight = Math.ceil(
    (Number(props.rows) + 0.5) * pxToNum(lineHeight) + pxToNum(paddingTop) + pxToNum(paddingBottom)
  );

  if (maxHeight < container.offsetHeight) {
    hasAction.value = true;
    text.value = calcEllipsisText(container, maxHeight);
  } else {
    hasAction.value = false;
    text.value = props.content;
  }
  document.body.removeChild(container);
}
const pxToNum = (value: string | null) => {
  if (!value) return 0;
  const match = value.match(/^\d*(\.\d*)?/);
  return match ? Number(match[0]) : 0;
};
function onClickAction() {
  expanded.value = !expanded.value;
}
</script>
<style lang="scss" scoped>
.action {
  margin-top: 16px;
  cursor: pointer;
  color: #157be1ff;
  font-size: 16px;
}
.text-box {
  line-height: 24px;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  font-size: 16px;
  // font-family: SF Pro Display, SF Pro Display;
  font-weight: 500;
  color: #000000;
  opacity: 0.5;
}
</style>
